import { Platform } from "react-native";
import * as FileSystem from "expo-file-system/legacy";

export type AttachmentFile = {
  id: string;
  name: string;
  size: number;
  mimeType: string | null;
  uri: string;
};

export const MAX_ATTACHMENTS = 5;

/** 10 MiB — keep in sync with s4p / spork-common leave request limits. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Canonical attachment formats. MIME list and extension maps are derived from this.
 * First extension is the preferred one used for generated filenames.
 */
const ATTACHMENT_FORMATS = [
  { mime: "application/pdf", extensions: ["pdf"] },
  { mime: "image/jpeg", extensions: ["jpg", "jpeg"] },
  { mime: "image/png", extensions: ["png"] },
  { mime: "image/webp", extensions: ["webp"] },
] as const;

export type AcceptedAttachmentMimeType =
  (typeof ATTACHMENT_FORMATS)[number]["mime"];

export const ACCEPTED_ATTACHMENT_MIME_TYPES: readonly AcceptedAttachmentMimeType[] =
  ATTACHMENT_FORMATS.map((format) => format.mime);

const EXTENSION_MIME_TYPES: Record<string, AcceptedAttachmentMimeType> =
  Object.fromEntries(
    ATTACHMENT_FORMATS.flatMap((format) =>
      format.extensions.map((extension) => [extension, format.mime] as const),
    ),
  ) as Record<string, AcceptedAttachmentMimeType>;

const MIME_TO_EXTENSION: Record<AcceptedAttachmentMimeType, string> =
  Object.fromEntries(
    ATTACHMENT_FORMATS.map((format) => [format.mime, format.extensions[0]]),
  ) as Record<AcceptedAttachmentMimeType, string>;

/** Non-canonical MIME values seen from pickers / browsers. */
const MIME_ALIASES: Record<string, AcceptedAttachmentMimeType> = {
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "application/x-pdf": "application/pdf",
};

function extensionOf(filename: string): string {
  const parts = filename.toLowerCase().split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

/**
 * Preferred file extension for a canonical attachment MIME type.
 */
export function extensionForMime(
  mimeType: AcceptedAttachmentMimeType | string | null | undefined,
): string {
  if (!mimeType) {
    return "bin";
  }

  return (
    MIME_TO_EXTENSION[mimeType as AcceptedAttachmentMimeType] ?? "bin"
  );
}

/**
 * Synthetic filename when a native picker omits `fileName`.
 */
export function fallbackAttachmentFilename(
  mimeType: AcceptedAttachmentMimeType,
): string {
  return `image-${Date.now()}.${extensionForMime(mimeType)}`;
}

/**
 * Normalize picker / browser MIME values (often null or aliases like image/jpg)
 * using the filename extension as fallback.
 */
export function resolveAttachmentMimeType(
  mimeType: string | null | undefined,
  filename: string,
): AcceptedAttachmentMimeType | null {
  const normalized = mimeType?.trim().toLowerCase() ?? "";

  if (normalized) {
    if (
      (ACCEPTED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(normalized)
    ) {
      return normalized as AcceptedAttachmentMimeType;
    }

    const alias = MIME_ALIASES[normalized];
    if (alias) {
      return alias;
    }
  }

  return EXTENSION_MIME_TYPES[extensionOf(filename)] ?? null;
}

/** FontAwesome 6 Pro regular icon for an attachment MIME / filename. */
export function iconForAttachment(
  mimeType: string | null | undefined,
  filename: string,
): "file-pdf" | "file-image" | "file" {
  const resolved = resolveAttachmentMimeType(mimeType, filename);

  if (resolved === "application/pdf") {
    return "file-pdf";
  }

  if (
    resolved === "image/jpeg" ||
    resolved === "image/png" ||
    resolved === "image/webp"
  ) {
    return "file-image";
  }

  return "file";
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function createAttachmentId(name: string, size: number): string {
  return `${name}-${size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Read a local (or blob) attachment URI as raw base64 for the s4p create payload.
 */
export async function readAttachmentAsBase64(uri: string): Promise<string> {
  if (Platform.OS === "web" || uri.startsWith("blob:")) {
    const response = await fetch(uri);
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result ?? ""));
      reader.onerror = () =>
        reject(reader.error ?? new Error("Failed to read attachment."));
      reader.readAsDataURL(blob);
    });

    return dataUrl.includes(",")
      ? dataUrl.slice(dataUrl.indexOf(",") + 1)
      : dataUrl;
  }

  return FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  });
}
